import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DomPurify from 'dompurify';
import { FormInputText } from '../../../inputs/FormInputText';
import {
  ButtonClassNames,
  InputTextFormat,
  ModalVariants,
  InputRegexPattern,
  LocalStorageItems,
} from '../../../../models/Common';
import { CustomMUIButton } from '../../buttons/CustomMUIButton';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { ApiError } from '../../../../models/ApiError';
import { ordersApi } from '../../../../services/apiServices/user/orders';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import { DataLayerEvents, DataLayerFormCategories } from '../../../../types/dataLayerTypes';
import { FormInputCheckbox } from '../../../inputs/FormInputCheckbox';
import { ReactComponent as GigImg } from '../../../../assets/images/gponShare/modalImage.svg';
import { ReactComponent as CheckBoxIcon } from '../../../../assets/images/EmptyCheckBox.svg';

interface CallbackProps {
  title?: string;
  service?: string;
  comment?: string;
  description?: string;
  isGigabitPromo?: boolean;
  serviceAnalyticsName?: string;
  bannerId?: number;
}

const defaultProps = {
  title: '',
  service: '',
  comment: '',
  description: '',
  isGigabitPromo: false,
  serviceAnalyticsName: undefined,
  bannerId: 0,
};

export const Callback: FC<CallbackProps> = ({
  title,
  description,
  service,
  comment,
  isGigabitPromo,
  serviceAnalyticsName,
  bannerId,
}) => {
  const { t } = useTranslation();
  const sanitizer = DomPurify.sanitize;
  const { userInfoState } = useAppSelector((state) => state.userInfoReducer);
  const { isAuth } = useAppSelector((state) => state.authReducer);
  const cachedData = localStorage.getItem(`${LocalStorageItems.CallMeBack}-${userInfoState.id}`);
  const showDefaults = isAuth && cachedData && JSON.parse(cachedData)[userInfoState.id];
  const defaultPhone =
    showDefaults && JSON.parse(cachedData)[userInfoState.id].phone
      ? JSON.parse(cachedData)[userInfoState.id].phone
      : '';
  const methods = useForm({
    defaultValues: {
      phone: userInfoState.SmsSettings?.Phone || defaultPhone,
      userComment: '',
      viber: false,
    },
  });
  const { handleSubmit, setError } = methods;
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const [authorizedCallback, authorizedCallbackState] = ordersApi.useAuthorizedCallbackMutation();
  const [unauthorizedCallback, unauthorizedCallbackState] = ordersApi.useUnauthorizedCallbackMutation();
  const isLoading = authorizedCallbackState.isLoading || unauthorizedCallbackState.isLoading;

  const handleFormSubmitAnalytics = (formTitle?: string, currentService?: string) => {
    const dataLayer = window.dataLayer || [];
    if (formTitle === t('callMeBack.callMeBack')) {
      dataLayer.push({
        event: DataLayerEvents.FORM_SUBMIT,
        formCategory: DataLayerFormCategories.CALL_ME,
      });
    } else {
      dataLayer.push({
        event: DataLayerEvents.FORM_SUBMIT,
        formCategory: DataLayerFormCategories.HOME_INTERNET,
        formRate: currentService,
      });
    }
  };

  const handleSaveFormData = (phone: string) => {
    const data: Partial<{ phone: string }> = {};

    if (phone !== userInfoState.SmsSettings?.Phone) {
      data.phone = phone;
    }
    if (data.phone) {
      localStorage.setItem(
        `${LocalStorageItems.CallMeBack}-${userInfoState.id}`,
        JSON.stringify({ [userInfoState.id]: data }),
      );
    } else {
      localStorage.removeItem(`${LocalStorageItems.CallMeBack}-${userInfoState.id}`);
    }
  };

  const handleSendAuthorizedRequest = async ({
    phone,
    userComment,
    viber,
  }: {
    phone: string;
    userComment: string;
    viber: boolean;
  }) => {
    const commentMessage = `${comment} Тел: ${phone}. ${viber ? `Дополнительная связь: ${viber ? 'Viber.' : ''}` : ''}${
      userComment.trim() ? ` Комментарий: ${userComment.trim()}` : ''
    }`;
    try {
      await authorizedCallback({ phone, comment: commentMessage, promoBannerId: bannerId ? bannerId.toString() : '' })
        .unwrap()
        .then(() => {
          dispatch(
            openModal({
              modalVariants: ModalVariants.Success,
              content: {
                title: `${t('modals.thankYou')}`,
                description: `${t('modals.requestAcceptedAnOperatorWillContactYouShortly')}`,
              },
            }),
          );
          handleSaveFormData(phone);
          handleFormSubmitAnalytics(title, serviceAnalyticsName);
        });
    } catch (err: unknown) {
      const error = err as ApiError;
      setError('phone', {
        type: 'server',
        message: error?.data?.error?.message,
      });
    }
  };

  const handleSendUnauthorizedRequest = async ({
    phone,
    userComment,
    viber,
  }: {
    phone: string;
    viber: boolean;
    userComment: string;
  }) => {
    const commentMessage = `Сайт: ${
      process.env.REACT_APP_NAME || ''
    }. Заявка на перезвон от неавторизованного пользователя. Телефон: ${phone}.${
      viber ? ` Дополнительная связь: ${viber ? 'Viber.' : ''}` : ''
    }${userComment.trim() ? ` Комментарий: ${userComment.trim()}` : ''}`;
    try {
      await unauthorizedCallback({ phone, comment: commentMessage })
        .unwrap()
        .then(() => {
          dispatch(
            openModal({
              modalVariants: ModalVariants.Success,
              content: {
                title: `${t('modals.thankYou')}`,
                description: `${t('modals.requestAcceptedAnOperatorWillContactYouShortly')}`,
              },
            }),
          );
          handleFormSubmitAnalytics(title);
        });
    } catch (err: unknown) {
      const error = err as ApiError;
      setError('phone', {
        type: 'server',
        message: error?.data?.error?.message,
      });
    }
  };

  return (
    <div className={`modals ${isGigabitPromo ? 'modals_gigabit-speed' : ''}`}>
      <div className="modals__header">
        {isGigabitPromo && <GigImg />}
        <article className="modals__header__title">{title}</article>
        {description && (
          <p
            className="modals__header__description"
            dangerouslySetInnerHTML={{
              __html: sanitizer(description),
            }}
          />
        )}
        {service && <p className="modals__header__description modals__header__description_accent">{service}</p>}
      </div>
      <FormProvider {...methods}>
        <form
          className="modals__form"
          onSubmit={handleSubmit(isAuth ? handleSendAuthorizedRequest : handleSendUnauthorizedRequest)}
        >
          <FormInputText
            name="phone"
            className="primary"
            label={t('input.phone')}
            isRequired
            minLength={10}
            format={InputTextFormat.Phone}
            pattern={InputRegexPattern.Phone}
          />
          <FormInputText name="userComment" className="primary" label={t('input.comment')} maxLength={100} />
          <div style={{ textAlign: 'left' }}>
            <p className="additional__connection__title">{t('callMeBack.additionalConnection')}</p>
            <FormInputCheckbox
              name="viber"
              icon={<CheckBoxIcon />}
              label={<div className="modals__form__label viber">{t('callMeBack.connectionViber')} </div>}
            />
          </div>
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('common.sendRequest')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isLoading={isLoading}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

Callback.defaultProps = defaultProps;
